import { Card, Carousel, theme, Flex, Typography, Select } from "antd";
import { Pie } from "@antv/g2plot";
import React, { useEffect, useState } from "react";
import Heatmap from "../common/Heatmap";
import useFetch from "../../hooks/useFetch";
import dayjs from "dayjs";
import DoughnutChart from "../common/DoughnutChart";

const { useToken } = theme;
const { Text, Title } = Typography;
const { Option } = Select;

function InsightCard({ themeToken, SiteID }) {
  const { token } = useToken();
  const [fetchWrapper] = useFetch();
  const [heatmapView, setHeatmapView] = useState("Area");
  const [heatmapData, setHeatmapData] = useState([]);
  const [peakOffpeakEnergyConsumption, setPeakOffpeakEnergyConsumption] =
    useState([]);
  const [areaEnergyConsumption, setAreaEnergyConsumption] = useState([]);
  const [seuEnergyConsumption, setSeuEnergyConsumption] = useState([]);

  function valueFormatter(value) {
    if (Math.abs(value) > 1000000) {
      return (
        (value / 1000000).toLocaleString({
          maximumFractionDigits: 2,
          minimumFractionDigits: 2,
        }) + " GWh"
      );
    } else if (Math.abs(value) > 1000) {
      return (
        (value / 1000).toLocaleString({
          maximumFractionDigits: 2,
          minimumFractionDigits: 2,
        }) + " MWh"
      );
    } else {
      return (
        value.toLocaleString({
          maximumFractionDigits: 2,
          minimumFractionDigits: 2,
        }) + " kWh"
      );
    }
  }

  function getHeatmapData() {
    fetchWrapper({
      method: "GET",
      endpoint_url: "dashboard/getHeatmapData",
      params: {
        SiteID: SiteID,
        View: heatmapView,
      },
      onSuccess: (response) => {
        setHeatmapData(response.data);
      },
    });
  }

  function getPeakOffpeakEnergyConsumption() {
    fetchWrapper({
      method: "GET",
      endpoint_url: "dashboard/getEnergyConsumptionInsight",
      params: {
        SiteID: SiteID,
        View: "PeakOffpeak",
      },
      onSuccess: (response) => {
        setPeakOffpeakEnergyConsumption(response.data);
      },
    });
  }

  function getAreaEnergyConsumption() {
    fetchWrapper({
      method: "GET",
      endpoint_url: "dashboard/getEnergyConsumptionInsight",
      params: {
        SiteID: SiteID,
        View: "Area",
      },
      onSuccess: (response) => {
        setAreaEnergyConsumption(response.data);
      },
    });
  }

  function getSeuEnergyConsumption() {
    fetchWrapper({
      method: "GET",
      endpoint_url: "dashboard/getEnergyConsumptionInsight",
      params: {
        SiteID: SiteID,
        View: "SEU",
      },
      onSuccess: (response) => {
        setSeuEnergyConsumption(response.data);
      },
    });
  }

  useEffect(() => {
    getHeatmapData();
    getPeakOffpeakEnergyConsumption();
    getAreaEnergyConsumption();
    getSeuEnergyConsumption();

    const timer = setInterval(() => {
      getHeatmapData();
      getPeakOffpeakEnergyConsumption();
      getAreaEnergyConsumption();
      getSeuEnergyConsumption();
    }, 300 * 1000);

    return () => {
      clearInterval(timer);
    };
  }, [SiteID]);

  useEffect(() => {
    getHeatmapData();
  }, [heatmapView]);

  return (
    <Card className="h-100" styles={{ body: { height: "100%" } }}>
      <Carousel
        autoplay={true}
        dotPosition="bottom"
        dots={{ className: "carousel" }}
        autoplaySpeed={10000}
        infinite={true}
      >
        <div className="h-100 w-100">
          <Flex align="center" justify="space-between">
            <Title level={5} className="mt-0 mb-0">
              Energy Heatmap
            </Title>
            <div className="w-100">
              <Text>View : </Text>
              <Select
                defaultValue={"Area"}
                style={{ width: 120, marginRight: 1 }}
                onChange={(value) => setHeatmapView(value)}
              >
                <Option value="Area">Area</Option>
                <Option value="SEU">SEUs</Option>
                <Option value="Device">Device</Option>
              </Select>
            </div>
          </Flex>
          <Heatmap
            data={heatmapData}
            themeToken={themeToken}
            height={550}
            yAxisTitle={heatmapView}
            xLabelFormatter={(d) =>
              dayjs(d).add(-8, "hour").format("D/M HH:mm")
            }
            xValueFormatter={(d) =>
              dayjs(d).add(-8, "hour").format("D/M/YY HH:mm")
            }
            colorValueFormatter={(d) => d?.toFixed(1) + " kWh"}
          />
        </div>
        <div className="h-100 w-100">
          <Flex align="center" justify="center">
            <Title level={5} className="mt-0 mb-3">
              Peak Vs Off-Peak Energy Consumption
            </Title>
          </Flex>
          <DoughnutChart
            data={peakOffpeakEnergyConsumption}
            y="value"
            color="name"
            innerRadius={0.55}
            outerRadius={0.8}
            statisticValueFormatter={(d) => valueFormatter(d)}
            yValueFormatter={(d) => valueFormatter(d)}
          />
        </div>
        <div className="h-100 w-100">
          <Flex align="center" justify="center">
            <Title level={5} className="mt-0 mb-3">
              Energy Consumption By Area
            </Title>
          </Flex>
          <DoughnutChart
            data={areaEnergyConsumption}
            y="value"
            color="name"
            innerRadius={0.55}
            outerRadius={0.8}
            statisticValueFormatter={(d) => valueFormatter(d)}
            yValueFormatter={(d) => valueFormatter(d)}
          />
        </div>
        <div className="h-100 w-100">
          <Flex align="center" justify="center">
            <Title level={5} className="mt-0 mb-3">
              Energy Consumption By SEU
            </Title>
          </Flex>
          <DoughnutChart
            data={seuEnergyConsumption}
            y="value"
            color="name"
            innerRadius={0.45}
            outerRadius={0.7}
            statisticValueFormatter={(d) => valueFormatter(d)}
            yValueFormatter={(d) => valueFormatter(d)}
          />
        </div>
      </Carousel>
    </Card>
  );
}

// function PeakAndOffPeakChart() {
//   const { token } = useToken();

//   const data = [
//     { type: "Peak", value: 212 },
//     { type: "Off-Peak", value: 25 },
//   ];

//   useEffect(() => {
//     document.getElementById("PeakAndOffPeakChart").innerHTML = "";

//     const piePlot = new Pie(
//       "PeakAndOffPeakChart",
//       {
//         appendPadding: 10,
//         data,
//         angleField: "value",
//         colorField: "type",
//         radius: 1,
//         innerRadius: 0.7,
//         meta: {
//           value: {
//             formatter: (v) => ` ${v} kWh `,
//           },
//         },
//         label: {
//           type: "inner",
//           offset: "-50%",
//           autoRotate: false,
//           style: { textAlign: "center" },
//           formatter: ({ percent }) => `${(percent * 100).toFixed(0)}%`,
//         },
//         statistic: {
//           title: {
//             style: { color: token.colorTextBase },
//             offsetY: -12,
//           },
//           content: {
//             style: {
//               fontSize: 28,
//               color: token.colorTextBase,
//               fontWeight: 500,
//             },
//             offsetY: -4,
//           },
//         },
//         legend: {
//           itemName: {
//             style: {
//               fontSize: 14,
//               color: token.colorTextBase,
//             },
//           },
//           style: {
//             color: token.colorTextBase,
//           },
//         },
//         // 添加 中心统计文本 交互
//         interactions: [
//           //{ type: 'element-selected' },
//           { type: "element-active" },
//           {
//             type: "pie-statistic-active",
//             cfg: {
//               start: [
//                 {
//                   trigger: "element:mouseenter",
//                   action: "pie-statistic:change",
//                 },
//                 {
//                   trigger: "legend-item:mouseenter",
//                   action: "pie-statistic:change",
//                 },
//               ],
//               end: [
//                 {
//                   trigger: "element:mouseleave",
//                   action: "pie-statistic:reset",
//                 },
//                 {
//                   trigger: "legend-item:mouseleave",
//                   action: "pie-statistic:reset",
//                 },
//               ],
//             },
//           },
//         ],
//       },
//       []
//     );

//     piePlot.render();
//   }, []);

//   return (
//     <Flex
//       justify="center"
//       align="center"
//       vertical={true}
//       style={{ paddingBottom: 32 }}
//     >
//       <Title level={5} className="mt-0 mb-5">
//         Energy Consumption By Time
//       </Title>
//       <div id="PeakAndOffPeakChart" style={{ height: 300 }} className="mb-3" />
//     </Flex>
//   );
// }

// function CatergoryChart() {
//   const { token } = useToken();

//   const data = [
//     { type: "HVAC", value: 212 },
//     { type: "Production", value: 123 },
//     { type: "Lighting", value: 34 },
//     { type: "Office", value: 53 },
//     { type: "Warehouse", value: 25 },
//   ];

//   useEffect(() => {
//     document.getElementById("CatergoryChart").innerHTML = "";

//     const piePlot = new Pie("CatergoryChart", {
//       appendPadding: 10,
//       data,
//       angleField: "value",
//       colorField: "type",
//       radius: 1,
//       innerRadius: 0.7,
//       meta: {
//         value: {
//           formatter: (v) => ` ${v} kWh `,
//         },
//       },
//       label: {
//         type: "inner",
//         offset: "-50%",
//         autoRotate: false,
//         style: { textAlign: "center" },
//         formatter: ({ percent }) => `${(percent * 100).toFixed(0)}%`,
//       },
//       statistic: {
//         title: {
//           style: { color: token.colorTextBase },
//           offsetY: -12,
//         },
//         content: {
//           style: { fontSize: 28, color: token.colorTextBase, fontWeight: 500 },
//           offsetY: -4,
//         },
//       },
//       legend: {
//         itemName: {
//           style: {
//             fontSize: 14,
//             color: token.colorTextBase,
//           },
//         },
//         style: {
//           color: token.colorTextBase,
//         },
//       },
//       // 添加 中心统计文本 交互
//       interactions: [
//         //{ type: 'element-selected' },
//         { type: "element-active" },
//         {
//           type: "pie-statistic-active",
//           cfg: {
//             start: [
//               { trigger: "element:mouseenter", action: "pie-statistic:change" },
//               {
//                 trigger: "legend-item:mouseenter",
//                 action: "pie-statistic:change",
//               },
//             ],
//             end: [
//               { trigger: "element:mouseleave", action: "pie-statistic:reset" },
//               {
//                 trigger: "legend-item:mouseleave",
//                 action: "pie-statistic:reset",
//               },
//             ],
//           },
//         },
//       ],
//     });

//     piePlot.render();
//   }, [token]);

//   return (
//     <Flex
//       justify="center"
//       align="center"
//       vertical={true}
//       style={{ paddingBottom: 32 }}
//     >
//       <Title level={5} className="mt-0 mb-5">
//         Energy Consumption By SEUs
//       </Title>
//       <div id="CatergoryChart" style={{ height: 300 }} />
//     </Flex>
//   );
// }

export default InsightCard;
